import { View } from "ui/atoms/view";
import { Text } from "react-native";
import { MedicationFormIcon } from "../atoms/MedicationFormIcon";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import { CheckIcon } from "ui/gluestack/icon";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import { useState } from "react";
import { MedicationModel } from "../../../services/api/types/medications/models/MedicationModel";
import { MedicationItemSummary } from "../atoms/MedicationItemSummary";
import { MedicationAdministrationStatusEnum } from "../../../services/api/types/medications/enums/MedicationAdministrationStatusEnum";
import { BulkMedicationsFormFields } from "../../../services/api/types/medications/models/BulkMedicationsFormFields";
import { Controller } from "react-hook-form";
import { MedicationStatusIndicator } from "../atoms/MedicationStatusIndicator";
import { useAdministerBulkMedicationsContext } from "../provider/AdministerBulkMedicationsProvider";

interface Props {
  medication: MedicationModel;
}

export function AdministerSkipMedicationItem({ medication }: Props) {
  const [status, setStatus] = useState<MedicationAdministrationStatusEnum>();
  const { control } = useAdministerBulkMedicationsContext();

  return (
    <View className="p-4 border border-gray-200 rounded-lg">
      <View className="flex flex-row gap-4 items-center">
        <MedicationFormIcon type={medication.form} />
        <View className="flex-1 gap-1">
          <Text className="text-base font-semibold text-typography-900">
            {medication.name}
          </Text>

          <View className="flex gap-y-2">
            <View className="flex-1 min-w-[200px]">
              <MedicationItemSummary
                medication={medication}
                showSignedBy={false}
              ></MedicationItemSummary>
            </View>

            <View className="flex flex-row flex-wrap items-center gap-x-9 gap-y-3">
              <Controller
                control={control}
                name={`medications.${medication.id}.status`}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    size="sm"
                    value="skipped"
                    isChecked={
                      value === MedicationAdministrationStatusEnum.SKIPPED
                    }
                    onChange={(checked) => {
                      const newStatus = checked
                        ? MedicationAdministrationStatusEnum.SKIPPED
                        : MedicationAdministrationStatusEnum.NONE;

                      onChange(newStatus);
                      setStatus(newStatus);
                    }}
                  >
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel className="ml-2">
                      <Text className="text-typography-600">Skipped</Text>
                    </CheckboxLabel>
                  </Checkbox>
                )}
              />
              <Controller
                control={control}
                name={`medications.${medication.id}.status`}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    size="sm"
                    value="self_administered"
                    isChecked={
                      value ===
                      MedicationAdministrationStatusEnum.SELF_ADMINISTERED
                    }
                    onChange={(checked) => {
                      const newStatus = checked
                        ? MedicationAdministrationStatusEnum.SELF_ADMINISTERED
                        : MedicationAdministrationStatusEnum.NONE;

                      onChange(newStatus);
                      setStatus(newStatus);
                    }}
                  >
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel className="ml-2">
                      <Text className="text-typography-600">
                        Self Administered
                      </Text>
                    </CheckboxLabel>
                  </Checkbox>
                )}
              />
              <Controller
                control={control}
                name={`medications.${medication.id}.status`}
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    size="sm"
                    value="administered"
                    isChecked={
                      value === MedicationAdministrationStatusEnum.ADMINISTERED
                    }
                    onChange={(checked) => {
                      const newStatus = checked
                        ? MedicationAdministrationStatusEnum.ADMINISTERED
                        : MedicationAdministrationStatusEnum.NONE;

                      onChange(newStatus);
                      setStatus(newStatus);
                    }}
                  >
                    <CheckboxIndicator>
                      <CheckboxIcon as={CheckIcon} />
                    </CheckboxIndicator>
                    <CheckboxLabel className="ml-2">
                      <Text className="text-typography-600">Administered</Text>
                    </CheckboxLabel>
                  </Checkbox>
                )}
              />
            </View>
          </View>
        </View>
        <View className="h-full justify-start">
          <MedicationStatusIndicator medication={medication} />
        </View>
      </View>
      {status === MedicationAdministrationStatusEnum.ADMINISTERED ||
      status === MedicationAdministrationStatusEnum.SELF_ADMINISTERED ? (
        <View>
          <View className="border-b border-gray-200 mt-6 mb-4" />
          <View className="flex flex-col sm:flex-row gap-2.5 pl-20">
            <View className="flex-1">
              <FormControlledInput<BulkMedicationsFormFields>
                id="med_dosage"
                name={`medications.${medication.id}.dosage`}
                control={control}
                label="Dosage and unit of measure"
                placeholder="Enter dosage"
                isRequired
              />
            </View>
            <View className="flex-1">
              <FormControlledInput<BulkMedicationsFormFields>
                id="med_notes"
                name={`medications.${medication.id}.other_information`}
                control={control}
                label="Note"
                placeholder="Enter note"
                className="text-gray-500"
              />
            </View>
          </View>
        </View>
      ) : null}
      {status === MedicationAdministrationStatusEnum.SKIPPED ? (
        <View>
          <View className="border-b border-gray-200 mt-6 mb-4" />
          <View className="flex pl-20">
            <FormControlledInput<BulkMedicationsFormFields>
              id="med_skip_reason"
              name={`medications.${medication.id}.skip_reason`}
              control={control}
              label="Reason"
              placeholder="Enter reason for skipping"
              isRequired
            />
          </View>
        </View>
      ) : null}
    </View>
  );
}
